import axios from "axios";
import apiConfig from "../config/api.js";

const API_URL = apiConfig.baseURL;

// Fonction helper pour obtenir le token
const getAuthHeader = () => {
  const token = localStorage.getItem("token");

  if (!token) {
    console.warn("⚠️  CommentService: Aucun token trouvé");
    return {};
  }

  console.log(
    "📤 CommentService: Token envoyé:",
    token.substring(0, 20) + "..."
  );

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

/**
 * Service pour gérer les commentaires sur les recettes
 */

const commentService = {
  /**
   * Créer un nouveau commentaire
   * @param {number} recipeId - ID de la recette
   * @param {string} commentText - Texte du commentaire
   * @param {number|null} rating - Note (1-5)
   * @returns {Promise} Commentaire créé
   */
  createComment: async (recipeId, commentText, rating = null) => {
    try {
      console.log("📝 Création commentaire:", {
        recipeId,
        commentText,
        rating,
      });

      const headers = getAuthHeader();
      console.log("📤 Headers:", headers);

      const response = await axios.post(
        `${API_URL}/comments`,
        {
          recipeId,
          commentText,
          rating,
        },
        { headers }
      );

      console.log("✅ Commentaire créé:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Erreur lors de la création du commentaire:", error);
      console.error("   Status:", error.response?.status);
      console.error("   Data:", error.response?.data);
      console.error("   Message:", error.message);
      throw error.response?.data || error;
    }
  },

  /**
   * Mettre à jour un commentaire
   * @param {number} commentId - ID du commentaire
   * @param {string} commentText - Texte du commentaire
   * @param {number|null} rating - Note (1-5)
   * @returns {Promise} Commentaire mis à jour
   */
  updateComment: async (commentId, commentText, rating = null) => {
    try {
      const response = await axios.put(
        `${API_URL}/comments/${commentId}`,
        {
          commentText,
          rating,
        },
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la modification du commentaire:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Supprimer un commentaire
   * @param {number} commentId - ID du commentaire
   * @returns {Promise} Résultat de la suppression
   */
  deleteComment: async (commentId) => {
    try {
      const response = await axios.delete(`${API_URL}/comments/${commentId}`, {
        headers: getAuthHeader(),
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la suppression du commentaire:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer les commentaires d'une recette
   * @param {number} recipeId - ID de la recette
   * @param {number} limit - Limite de résultats
   * @returns {Promise} Liste des commentaires
   */
  getRecipeComments: async (recipeId, limit = 50) => {
    try {
      const response = await axios.get(
        `${API_URL}/comments/recipe/${recipeId}`,
        {
          params: { limit },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des commentaires:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer les commentaires d'un utilisateur
   * @param {number} userId - ID de l'utilisateur
   * @param {number} limit - Limite de résultats
   * @returns {Promise} Liste des commentaires
   */
  getUserComments: async (userId, limit = 50) => {
    try {
      const response = await axios.get(`${API_URL}/comments/user/${userId}`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des commentaires:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Liker un commentaire
   * @param {number} commentId - ID du commentaire
   * @returns {Promise} Résultat du like
   */
  likeComment: async (commentId) => {
    try {
      const response = await axios.post(
        `${API_URL}/comments/${commentId}/like`,
        {},
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors du like:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Retirer le like d'un commentaire
   * @param {number} commentId - ID du commentaire
   * @returns {Promise} Résultat du unlike
   */
  unlikeComment: async (commentId) => {
    try {
      const response = await axios.delete(
        `${API_URL}/comments/${commentId}/like`,
        {
          headers: getAuthHeader(),
        }
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors du retrait du like:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Obtenir les statistiques d'une recette
   * @param {number} recipeId - ID de la recette
   * @returns {Promise} Statistiques
   */
  getRecipeStats: async (recipeId) => {
    try {
      const response = await axios.get(
        `${API_URL}/comments/recipe/${recipeId}/stats`
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des statistiques:", error);
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer les commentaires récents de la communauté
   * @param {number} limit - Limite de résultats
   * @returns {Promise} Liste des commentaires récents
   */
  getRecentCommunityComments: async (limit = 20) => {
    try {
      const response = await axios.get(`${API_URL}/comments/recent`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des commentaires récents:",
        error
      );
      throw error.response?.data || error;
    }
  },
};

export default commentService;
